import { useState, useEffect, useCallback, memo, useRef } from 'react';
import { Copy, RotateCcw, Trash2, Expand } from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { CategoryCombobox } from './CategoryCombobox';
import { VendorCombobox } from './VendorCombobox';
import { LineItemDescriptionDialog } from './LineItemDescriptionDialog';
import { formatNumber, getMarginColor, cn, chfToPresentation } from '../lib/utils';

const CURRENCIES = ['CHF', 'EUR', 'USD'];
const DISCOUNT_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'percent', label: '%' },
  { value: 'amount', label: 'Amt' },
];

function draftFromItem(item) {
  return {
    category: item.category,
    vendor: item.vendor || '',
    qty: item.qty,
    revenue_unit: item.revenue_unit,
    revenue_currency: item.revenue_currency,
    discount_type: item.discount_type || 'none',
    discount_value: item.discount_value || 0,
    cost_unit: item.cost_unit,
    cost_currency: item.cost_currency,
  };
}

function draftToUpdatePayload(draft) {
  return {
    category: draft.category,
    vendor: draft.vendor,
    qty: parseFloat(draft.qty) || 0,
    revenue_unit: parseFloat(draft.revenue_unit) || 0,
    revenue_currency: draft.revenue_currency,
    discount_type: draft.discount_type,
    discount_value: parseFloat(draft.discount_value) || 0,
    cost_unit: parseFloat(draft.cost_unit) || 0,
    cost_currency: draft.cost_currency,
  };
}

export const LineItemRow = memo(function LineItemRow({
  item,
  categories,
  vendors,
  onUpdate,
  onDelete,
  onDuplicate,
  onRestoreItem,
  onCreateCategory,
  onDeleteCategory,
  onCreateVendor,
  disabled,
  nameLayout = 'compact',
  selectable = false,
  selected = false,
  onToggleSelect,
  canSelect = true,
  marginTarget,
  presentationCurrency,
  vendorCurrency,
  coreCurrency = 'CHF',
  fxRates,
  visibleColumns,
}) {
  const vis = (id) => !visibleColumns || !visibleColumns.has(id);
  const showVendorView = vendorCurrency && vendorCurrency !== coreCurrency;
  // Local editing state to prevent input reset while typing.
  // The ref is the draft the next commit reads, so a second change in the
  // same turn is not built from the previous render's state.
  const [localState, setLocalState] = useState(() => draftFromItem(item));
  const localStateRef = useRef(localState);

  const [titleDraft, setTitleDraft] = useState(() => String(item.title || item.name || '').trim());

  // Sync from server when item identity or saved fields change (e.g. after PUT line item).
  // Depend on those fields, not the whole item, so description edits do not reset in-progress inputs.
  useEffect(() => {
    const nextDraft = draftFromItem({
      category: item.category,
      vendor: item.vendor,
      qty: item.qty,
      revenue_unit: item.revenue_unit,
      revenue_currency: item.revenue_currency,
      discount_type: item.discount_type,
      discount_value: item.discount_value,
      cost_unit: item.cost_unit,
      cost_currency: item.cost_currency,
    });
    localStateRef.current = nextDraft;
    setLocalState(nextDraft);
  }, [item.id, item.revenue_unit, item.cost_unit, item.qty, item.category, item.vendor, item.revenue_currency, item.discount_type, item.discount_value, item.cost_currency]);

  useEffect(() => {
    setTitleDraft(String(item.title || item.name || '').trim());
  }, [item.id, item.title, item.name]);

  const [descOpen, setDescOpen] = useState(false);
  const [descSaving, setDescSaving] = useState(false);

  const updateDraftField = (field, value) => {
    const nextDraft = { ...localStateRef.current, [field]: value };
    localStateRef.current = nextDraft;
    setLocalState(nextDraft);
    return nextDraft;
  };

  const commitChanges = useCallback(() => {
    onUpdate(item.id, draftToUpdatePayload(localStateRef.current));
  }, [item.id, onUpdate]);

  const commitTitleDraft = useCallback(() => {
    const next = titleDraft.trim() || 'Untitled';
    const cur = String(item.title || item.name || 'Untitled').trim();
    if (next !== cur) {
      onUpdate(item.id, { title: next, name: next });
    }
  }, [titleDraft, item.id, item.title, item.name, onUpdate]);

  const handleChange = (field, value) => {
    updateDraftField(field, value);
  };

  const handleBlur = () => {
    commitChanges();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const handleSelectChange = (field, value) => {
    updateDraftField(field, value);
    commitChanges();
  };

  const handleCategoryChange = (value) => {
    updateDraftField('category', value);
    commitChanges();
  };

  const handleVendorChange = (value) => {
    updateDraftField('vendor', value);
    commitChanges();
  };

  const titleRaw = String(item.title || item.name || 'Untitled').trim();
  /** Row hint: summarize description_plain (few lines × middot), not first line only. */
  const plainFull = String(item.description_plain || '').trim();
  const descCollapsedPreview = (() => {
    if (!plainFull) return '';
    const lines = plainFull.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    const joined = lines.slice(0, 5).join(' · ');
    const maxLen = 160;
    if (joined.length <= maxLen) return joined;
    return `${joined.slice(0, maxLen).trim()}…`;
  })();
  const showDescHint = Boolean(descCollapsedPreview && descCollapsedPreview !== titleRaw);
  const descHintPreview = descCollapsedPreview;

  return (
    <>
    <tr 
      className={`pl-row transition-colors duration-150 ease-in-out hover:bg-gray-50/60 ${selected ? 'bg-sky-50/60' : ''}`}
      data-testid={`line-item-row-${item.id}`}
    >
      {selectable ? (
        <td className="border-b border-gray-100 px-1 text-center align-top">
          <Checkbox
            checked={selected}
            onCheckedChange={(v) => onToggleSelect?.(item.id, v === true)}
            disabled={disabled || !canSelect}
            aria-label={`Select ${titleRaw || 'item'}`}
            data-testid={`catalog-select-${item.id}`}
            className="mt-1.5"
          />
        </td>
      ) : null}

      {/* Name */}
      {vis('name') && (
      <td className={`border-b border-gray-100 px-2.5 text-left align-top ${nameLayout === 'catalog' ? 'whitespace-normal' : 'whitespace-nowrap'}`}>
        <div className="flex items-start gap-2">
          <div className={`min-w-0 flex-1 ${nameLayout === 'catalog' ? '' : 'max-w-[240px]'}`}>
            <Input
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={commitTitleDraft}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.target.blur();
                }
              }}
              disabled={disabled}
              placeholder="Line title"
              title={nameLayout === 'catalog' ? titleDraft : undefined}
              className={`text-sm font-medium px-2 py-0 border border-transparent hover:border-gray-200 focus:border-gray-300 ${
                nameLayout === 'catalog' ? 'h-auto min-h-7 whitespace-normal leading-snug' : 'h-7'
              }`}
              data-testid={`line-item-name-${item.id}`}
            />
            {showDescHint ? (
              <div
                className="truncate text-[11px] text-gray-500 leading-tight mt-0.5"
                title={plainFull || undefined}
                data-testid={`line-item-desc-hint-${item.id}`}
              >
                {descHintPreview}
              </div>
            ) : null}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setDescOpen(true)}
            disabled={disabled}
            data-testid={`line-item-expand-${item.id}`}
          >
            <Expand className="h-3.5 w-3.5" />
          </Button>
        </div>
      </td>
      )}

      {/* Category */}
      {vis('category') && (
      <td className="border-b border-gray-100 px-2.5 text-left whitespace-nowrap">
        <CategoryCombobox
          value={localState.category}
          categories={categories}
          onChange={handleCategoryChange}
          onCreateCategory={onCreateCategory}
          onDeleteCategory={onDeleteCategory}
          disabled={disabled}
        />
      </td>
      )}

      {/* Qty */}
      {vis('qty') && (
      <td className="border-b border-gray-100 px-2 text-center whitespace-nowrap">
        <Input
          type="number"
          value={localState.qty}
          onChange={(e) => handleChange('qty', e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className="inline-input w-12 text-center tabular-nums text-[11px]"
          data-testid={`line-item-qty-${item.id}`}
        />
      </td>
      )}

      {/* CLIENT VIEW: Rev/Unit in presentation currency */}
      {presentationCurrency !== coreCurrency && vis('client_rev_unit') && (
          <td className="border-b border-l border-blue-50 border-l-blue-100 bg-blue-50/20 px-2.5 text-right tabular-nums text-[12px] text-blue-700 whitespace-nowrap" data-testid={`line-item-client-rev-unit-${item.id}`}>
            {formatNumber(chfToPresentation(item.revenue_chf / (item.qty || 1), presentationCurrency, fxRates, coreCurrency), 2)}
          </td>
      )}
      {presentationCurrency !== coreCurrency && vis('client_rev_total') && (
          <td className="border-b border-blue-50 bg-blue-50/20 px-2.5 text-right tabular-nums text-[12px] font-semibold text-blue-700 whitespace-nowrap" data-testid={`line-item-client-rev-total-${item.id}`}>
            {formatNumber(chfToPresentation(item.revenue_chf, presentationCurrency, fxRates, coreCurrency))}
          </td>
      )}

      {/* Rev/Unit + Auto pricing */}
      {vis('rev_unit') && (
      <td className="border-b border-l border-gray-100 border-l-gray-100 px-2 text-right whitespace-nowrap">
        <Input
          type="number"
          value={localState.revenue_unit}
          onChange={(e) => handleChange('revenue_unit', e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className="inline-input w-[72px] text-right tabular-nums text-[11px]"
          data-testid={`line-item-rev-unit-${item.id}`}
        />
      </td>
      )}

      {/* Rev Currency */}
      {vis('rev_cur') && (
      <td className="border-b border-gray-100 px-1 text-center whitespace-nowrap">
        <Select
          value={localState.revenue_currency}
          onValueChange={(v) => handleSelectChange('revenue_currency', v)}
          disabled={disabled}
        >
          <SelectTrigger 
            className="h-6 w-[66px] px-1.5 text-[10px] font-medium border-transparent bg-transparent"
            data-testid={`line-item-rev-cur-${item.id}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CURRENCIES.map(c => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      )}

      {vis('discount_type') && (
      <td className="border-b border-gray-100 px-1 text-center whitespace-nowrap">
        <Select
          value={localState.discount_type}
          onValueChange={(v) => handleSelectChange('discount_type', v)}
          disabled={disabled}
        >
          <SelectTrigger className="h-6 w-[74px] px-1.5 text-[10px] font-medium border-transparent bg-transparent">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DISCOUNT_TYPES.map((d) => (
              <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      )}

      {vis('discount_value') && (
      <td className="border-b border-gray-100 px-2 text-right whitespace-nowrap">
        <Input
          type="number"
          value={localState.discount_value}
          onChange={(e) => handleChange('discount_value', e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled || localState.discount_type === 'none'}
          className="inline-input w-[66px] text-right tabular-nums text-[11px]"
        />
      </td>
      )}

      {/* Rev CHF (computed) */}
      {vis('rev_chf') && (
      <td className="border-b border-gray-100 px-2.5 text-right tabular-nums text-[12px] whitespace-nowrap" data-testid={`line-item-rev-chf-${item.id}`}>
        <div>{formatNumber(item.revenue_chf)} CHF</div>
        <div className="text-[10px] text-gray-500">
          G {formatNumber(item.gross_revenue_chf || item.revenue_chf)} · D {formatNumber(item.line_discount_chf || 0)}
        </div>
      </td>
      )}

      {/* Cost/Unit + Auto cost */}
      {vis('cost_unit') && (
      <td className="border-b border-l border-gray-100 border-l-gray-100 px-2 text-right whitespace-nowrap">
        <Input
          type="number"
          value={localState.cost_unit}
          onChange={(e) => handleChange('cost_unit', e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className="inline-input w-[72px] text-right tabular-nums text-[11px]"
          data-testid={`line-item-cost-unit-${item.id}`}
        />
      </td>
      )}

      {/* Cost Currency */}
      {vis('cost_cur') && (
      <td className="border-b border-gray-100 px-1 text-center whitespace-nowrap">
        <Select
          value={localState.cost_currency}
          onValueChange={(v) => handleSelectChange('cost_currency', v)}
          disabled={disabled}
        >
          <SelectTrigger 
            className="h-6 w-[66px] px-1.5 text-[10px] font-medium border-transparent bg-transparent"
            data-testid={`line-item-cost-cur-${item.id}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CURRENCIES.map(c => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      )}

      {/* Cost CHF (computed) */}
      {vis('cost_chf') && (
      <td className="border-b border-gray-100 px-2.5 text-right tabular-nums text-[12px] whitespace-nowrap" data-testid={`line-item-cost-chf-${item.id}`}>
        {formatNumber(item.cost_chf)} CHF
        {showVendorView && (
          <span className="ml-1 text-[11px] text-orange-600" data-testid={`line-item-cost-vendor-${item.id}`}>
            ({formatNumber(chfToPresentation(item.cost_chf, vendorCurrency, fxRates, coreCurrency))} {vendorCurrency})
          </span>
        )}
      </td>
      )}

      {/* Vendor (cost-side tagging) */}
      {vis('vendor') && (
      <td className="border-b border-gray-100 px-2.5 text-left whitespace-nowrap">
        <VendorCombobox
          value={localState.vendor}
          vendors={vendors}
          onChange={handleVendorChange}
          onCreateVendor={onCreateVendor}
          disabled={disabled}
        />
      </td>
      )}

      {/* Margin CHF (computed) */}
      {vis('margin_chf') && (
      <td className="border-b border-l border-gray-100 border-l-gray-100 px-2.5 text-right tabular-nums text-[12px] whitespace-nowrap" data-testid={`line-item-margin-chf-${item.id}`}>
        {formatNumber(item.margin_chf)} CHF
      </td>
      )}

      {/* Margin % (computed) */}
      {vis('margin_pct') && (
      <td className={cn(
        "border-b border-gray-100 px-2.5 text-right tabular-nums text-[12px] font-semibold whitespace-nowrap",
        getMarginColor(item.margin_pct, marginTarget)
      )} data-testid={`line-item-margin-pct-${item.id}`}>
        {item.margin_pct.toFixed(1)}%
      </td>
      )}

      {/* Actions */}
      {vis('actions') && (
      <td className="border-b border-gray-100 px-2 text-right">
        <div className="flex justify-end gap-1">
          {item.is_active === false && onRestoreItem ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onRestoreItem(item.id)}
              disabled={disabled}
              className="h-7 w-7 text-emerald-600 hover:bg-emerald-50"
              title="Reactivate"
              data-testid={`line-item-restore-${item.id}`}
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDuplicate(item.id)}
            disabled={disabled}
            className="h-7 w-7"
            data-testid={`line-item-duplicate-${item.id}`}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(item.id)}
            disabled={disabled}
            className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50"
            title={item.is_active === false ? 'Delete permanently' : 'Deactivate'}
            data-testid={`line-item-delete-${item.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </td>
      )}
    </tr>
    <LineItemDescriptionDialog
      open={descOpen}
      onOpenChange={setDescOpen}
      initialHtml={item.description_html || ''}
      initialPlain={item.description_plain || ''}
      initialTitle={item.title || item.name || 'Untitled'}
      saving={descSaving}
      onSave={async ({ title, description_html, description_plain }) => {
        setDescSaving(true);
        try {
          await onUpdate(item.id, {
            title,
            name: title,
            description_html,
            description_plain,
          });
          setDescOpen(false);
        } finally {
          setDescSaving(false);
        }
      }}
    />
    </>
  );
});
